import React, { useState, useEffect } from "react";
import api from "../../api";
import { SyncStatusBadge } from "../plaid";
import useModalEscape from "@/hooks/useModalEscape";

/**
 * SnapTradeAccountSettings - Modal for managing a SnapTrade-connected account.
 *
 * Shows brokerage name, account mask, connection status, last sync time.
 * Provides "Sync Now" and "Disconnect" actions.
 *
 * @param {string}   accountId - The WealthPort account ID
 * @param {function} onClose   - Callback to close the modal
 * @param {function} onUpdate  - Callback after sync or disconnect
 */
const SnapTradeAccountSettings = ({ accountId, onClose, onUpdate }) => {
  useModalEscape(onClose);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState(null);

  // Load connection status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/accounts/${accountId}/snaptrade-status`);
        setStatus(response.data);
      } catch (err) {
        console.error("Error fetching SnapTrade status:", err);
        setError(err.response?.data?.detail || "Failed to load SnapTrade status");
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [accountId]);

  // Manual sync
  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);

      await api.post(`/accounts/${accountId}/sync-snaptrade`);

      setStatus((prev) => ({
        ...prev,
        last_sync: new Date().toISOString(),
      }));

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error("Error syncing SnapTrade:", err);
      setError(err.response?.data?.detail || "Failed to sync account");
    } finally {
      setSyncing(false);
    }
  };

  // Disconnect
  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect this account from SnapTrade? " +
          "Your transaction history will be preserved, but automatic syncing will stop."
      )
    ) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);

      await api.delete(`/accounts/${accountId}/disconnect-snaptrade`);

      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      console.error("Error disconnecting SnapTrade:", err);
      setError(err.response?.data?.detail || "Failed to disconnect");
    } finally {
      setDisconnecting(false);
    }
  };

  // Loading spinner
  if (loading) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-center py-8">
            <svg
              className="animate-spin h-8 w-8 text-green-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // Not connected
  if (!status || !status.connected) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            SnapTrade Settings
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This account is not connected to SnapTrade.
          </p>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            SnapTrade Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Connection Info */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Connected Account
            </label>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🏦</span>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {status.snaptrade_account_name || "Brokerage Account"}
                  {status.snaptrade_account_mask && (
                    <span className="ml-1 text-gray-500 dark:text-gray-400 font-normal">
                      ···{status.snaptrade_account_mask}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Brokerage Badge */}
          {status.snaptrade_brokerage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Brokerage
              </label>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                {status.snaptrade_brokerage}
              </span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sync Status
            </label>
            <SyncStatusBadge lastSync={status.last_sync} isSyncing={syncing} />
          </div>

          {status.connected_at && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Connected
              </label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {new Date(status.connected_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleSync}
            disabled={syncing || disconnecting}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {syncing ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Sync Now</span>
              </>
            )}
          </button>

          <button
            onClick={handleDisconnect}
            disabled={syncing || disconnecting}
            className="w-full px-4 py-2 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {disconnecting ? "Disconnecting..." : "Disconnect SnapTrade"}
          </button>
        </div>

        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
          Disconnecting preserves your transaction history but stops automatic
          syncing.
        </p>
      </div>
    </div>
  );
};

export default SnapTradeAccountSettings;
